/**
 * GrantEditFormBody — shared form body for the GRANT / REVOKE inline popover
 * (Slice F2). Supports a mode toggle inside the form so the user can flip
 * between GRANT and REVOKE without re-opening the popover.
 *
 * Engines:
 *   - mssql / postgres / mysql / oracle: supported.
 *   - sqlite: rejected — surface disabled with documented tooltip.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice F2.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import {
  commitGrant,
  isGrantSupported,
  validateGrantForEngine,
} from '../../edit/edits/grant';
import {
  commitRevoke,
  isRevokeSupported,
  validateRevokeForEngine,
} from '../../edit/edits/revoke';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { GrantFormFields } from './GrantFormFields';
import {
  buildGrantDef,
  buildRevokeDef,
  DEFAULT_GRANT_FORM_VALUES,
  getGrantUnsupportedTooltip,
  isGrantSupportedForEngine,
  type GrantFormValues,
} from './grantFormHelpers';
import { FormActionsSlot } from './FormActionsSlot';
import { FormEngineChip } from './FormEngineChip';

export interface GrantEditFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly initialValues?: Partial<GrantFormValues>;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function GrantEditFormBody(props: GrantEditFormBodyProps) {
  const { editContext, initialValues, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;

  const [values, setValues] = useState<GrantFormValues>({
    ...DEFAULT_GRANT_FORM_VALUES, ...initialValues,
  });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!isGrantSupportedForEngine(engine)) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <div className="text-xs font-semibold">Privileges</div>
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-unsupported`}
        >
          {getGrantUnsupportedTooltip(engine)}
        </div>
        <div className="flex items-center justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onCancel?.()}
            data-testid={`${testId}-close`}
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  async function handleSubmit() {
    setError(null);
    if (values.mode === 'grant') {
      if (!isGrantSupported(engine)) {
        setError(getGrantUnsupportedTooltip(engine));
        return;
      }
      const grant = buildGrantDef(engine, values);
      const v = validateGrantForEngine(engine, { grant });
      if (v) { setError(v); return; }
      setPending(true);
      try { await commitGrant(editContext, { grant }); onCommitted?.(); }
      catch (e) { setError(toMessage(e)); setPending(false); }
      return;
    }
    if (!isRevokeSupported(engine)) {
      setError(getGrantUnsupportedTooltip(engine));
      return;
    }
    const revoke = buildRevokeDef(engine, values);
    const v = validateRevokeForEngine(engine, { revoke });
    if (v) { setError(v); return; }
    setPending(true);
    try { await commitRevoke(editContext, { revoke }); onCommitted?.(); }
    catch (e) { setError(toMessage(e)); setPending(false); }
  }

  const submitLabel = pending
    ? 'Saving…'
    : values.mode === 'grant' ? 'Apply GRANT' : 'Apply REVOKE';

  return (
    <div data-testid={testId} className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Privileges</div>
        <FormEngineChip engine={engine} />
      </div>

      <GrantFormFields
        engine={engine}
        values={values}
        setValues={setValues}
        testId={testId}
      />

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <FormActionsSlot className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {submitLabel}
        </Button>
      </FormActionsSlot>
    </div>
  );
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
